// The control-plane wire protocol — the TEXT frames on a Munos
// WebSocket. (Binary frames are opaque script payloads and never come
// through here.)
//
// Two messages exist today:
//   - redirect: server-initiated, the counterpart of a script-side hop()
//     where the SERVER picks the URL and handoff.
//   - peer_left: the interest-management leave signal — the host tells a
//     receiving client a peer left its interest set (AoI cull / disconnect /
//     timeout), so the client's `event peer_left(slot)` drops the ghost
//     cleanly instead of dead-reckoning a phantom. See
//     docs/interest-management.md.
//
// The server encodes with encodeControlMessage and the client decodes with
// decodeControlMessage, so the two sides cannot drift — adding a control
// message means extending the union and the codec here, once, instead of
// editing a JSON.stringify on one side and a hand-rolled type guard on the
// other.
export function encodeControlMessage(msg) {
    if (msg.type === "peer_left") {
        return JSON.stringify({ type: "peer_left", slot: msg.slot });
    }
    if (msg.type === "host_locally") {
        return JSON.stringify({
            type: "host_locally",
            parentUrl: msg.parentUrl,
            handoff: bytesToBase64(msg.handoff),
        });
    }
    return JSON.stringify({
        type: msg.type,
        url: msg.url,
        handoff: bytesToBase64(msg.handoff),
    });
}
// Returns null for anything that isn't a well-formed control message
// (clients drop unknown text frames silently — a newer server may
// speak messages an older client doesn't know).
export function decodeControlMessage(raw) {
    let parsed;
    try {
        parsed = JSON.parse(raw);
    }
    catch {
        return null;
    }
    if (typeof parsed !== "object" || parsed === null)
        return null;
    const msg = parsed;
    if (msg.type === "peer_left") {
        if (typeof msg.slot !== "number" || !Number.isFinite(msg.slot))
            return null;
        return { type: "peer_left", slot: msg.slot | 0 };
    }
    if (msg.type === "host_locally") {
        if (typeof msg.parentUrl !== "string")
            return null;
        const handoff = typeof msg.handoff === "string"
            ? base64ToBytes(msg.handoff)
            : new Uint8Array(0);
        return { type: "host_locally", parentUrl: msg.parentUrl, handoff };
    }
    if (msg.type !== "redirect" || typeof msg.url !== "string")
        return null;
    const handoff = typeof msg.handoff === "string"
        ? base64ToBytes(msg.handoff)
        : new Uint8Array(0);
    return { type: "redirect", url: msg.url, handoff };
}
// Resolve a redirect destination — absolute (ws://… / wss://…) or
// server-relative (`/<instance-id>`) — against the current connection's full
// URL. Shared by the server-redirect path, script-side hop, and spawn_local,
// so a script can name a sibling deployment by bare path regardless of which
// one it is. Lives here (next to the redirect codec) so every client — the
// browser host AND the fidelity-rig client — resolves it identically.
export function resolveRedirectUrl(currentUrl, redirectUrl) {
    if (/^wss?:\/\//i.test(redirectUrl))
        return redirectUrl;
    try {
        const base = new URL(currentUrl);
        base.pathname = redirectUrl.startsWith("/") ? redirectUrl : `/${redirectUrl}`;
        base.search = "";
        return base.toString();
    }
    catch {
        return redirectUrl;
    }
}
// btoa/atob are global in every browser and in Node 16+; chunked so
// large handoffs don't blow the String.fromCharCode argument limit.
function bytesToBase64(bytes) {
    let s = "";
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
        s += String.fromCharCode(...bytes.subarray(i, Math.min(i + CHUNK, bytes.length)));
    }
    return btoa(s);
}
function base64ToBytes(b64) {
    try {
        // Strip whitespace first (matches the canonical decodeBase64 in util.ts):
        // a MIME/line-wrapped handoff from an intermediary would otherwise throw
        // in atob and be silently dropped.
        const bin = atob(b64.replace(/\s+/g, ""));
        const out = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++)
            out[i] = bin.charCodeAt(i) & 0xff;
        return out;
    }
    catch (e) {
        // Surface the failure instead of silently delivering an empty handoff —
        // a corrupt handoff is a real error, not a zero-length payload.
        console.error("munos: failed to decode base64 handoff", e);
        return new Uint8Array(0);
    }
}
//# sourceMappingURL=control-protocol.js.map